import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Star, 
  MapPin, 
  Phone,
  Sparkles,
  Trash2,
  Loader2,
  Utensils,
  Car,
  Scissors,
  Wrench,
  Home,
  Briefcase,
  Heart,
  TreeDeciduous,
  Coffee,
  ShoppingBag,
  Stethoscope,
  GraduationCap,
  Dumbbell,
  PawPrint,
  Landmark,
  Store,
  Building2,
  CheckCircle2,
  Globe,
  Palette,
  Code,
  FileText,
  CheckSquare,
  Square
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category icon mapping
const categoryIcons = {
  restaurant: Utensils,
  food: Utensils,
  cafe: Coffee,
  coffee: Coffee,
  car_repair: Car,
  car_dealer: Car,
  car_wash: Car,
  beauty_salon: Scissors,
  hair_care: Scissors,
  spa: Heart,
  plumber: Wrench,
  electrician: Wrench,
  contractor: Wrench,
  real_estate_agency: Home,
  real_estate: Home,
  lawyer: Briefcase,
  accounting: Briefcase,
  insurance_agency: Briefcase,
  doctor: Stethoscope,
  dentist: Stethoscope,
  hospital: Stethoscope,
  pharmacy: Stethoscope,
  veterinary_care: PawPrint,
  pet_store: PawPrint,
  gym: Dumbbell,
  fitness: Dumbbell,
  school: GraduationCap,
  university: GraduationCap,
  bank: Landmark,
  landscaping: TreeDeciduous,
  garden: TreeDeciduous,
  store: Store,
  shopping: ShoppingBag,
  default: Building2
};

const formatCategory = (category) => {
  if (!category) return 'Business';
  return category
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

// Format address with proper capitalization
const formatAddress = (address) => {
  if (!address) return '';
  
  // First, handle UK postcodes - they should be fully uppercase
  // UK postcode pattern: 1-2 letters, 1-2 digits, optional space, digit, 2 letters
  let result = address.replace(
    /\b([A-Za-z]{1,2}[0-9][0-9A-Za-z]?\s*[0-9][A-Za-z]{2})\b/gi,
    (match) => match.toUpperCase()
  );
  
  // Then capitalize each word (but not postcodes since they're already uppercase)
  result = result
    .split(' ')
    .map(word => {
      // If it's already all uppercase (like a postcode part), keep it
      if (word === word.toUpperCase() && word.length <= 4) {
        return word;
      }
      // Capitalize first letter of each word
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
  
  // Fix common abbreviations
  return result
    .replace(/\bUk\b/g, 'UK')
    .replace(/\bNw(\d)/gi, 'NW$1')
    .replace(/\bSw(\d)/gi, 'SW$1')
    .replace(/\bSe(\d)/gi, 'SE$1')
    .replace(/\bNe(\d)/gi, 'NE$1')
    .replace(/\bEc(\d)/gi, 'EC$1')
    .replace(/\bWc(\d)/gi, 'WC$1');
};

const getCategoryIcon = (category) => {
  if (!category) return categoryIcons.default;
  const lowerCategory = category.toLowerCase();
  for (const [key, icon] of Object.entries(categoryIcons)) {
    if (lowerCategory.includes(key)) {
      return icon;
    }
  }
  return categoryIcons.default;
};

const Favourites = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [favourites, setFavourites] = useState([]);
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [removing, setRemoving] = useState(null);
  // Bulk generation state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, currentName: '' });
  const [generationProgress, setGenerationProgress] = useState({
    show: false,
    businessName: '',
    currentStep: 0,
    // Bulk mode additions
    isBulk: false,
    bulkCurrent: 0,
    bulkTotal: 0,
    steps: [
      { id: 1, name: 'Analyzing business info', icon: FileText },
      { id: 2, name: 'Creating design layout', icon: Palette },
      { id: 3, name: 'Generating content', icon: Code },
      { id: 4, name: 'Building website', icon: Globe },
      { id: 5, name: 'Finalizing', icon: CheckCircle2 }
    ]
  });

  const headers = { Authorization: `Bearer ${token}` };

  const MAX_BULK_GENERATE = 5;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [favResponse, webResponse] = await Promise.all([
        axios.get(`${API}/favourites`, { headers }),
        axios.get(`${API}/websites`, { headers })
      ]);
      // Filter out favourites that already have websites generated
      const websiteBusinessIds = webResponse.data.map(w => w.business_id);
      const pendingFavourites = favResponse.data.filter(
        f => !websiteBusinessIds.includes(f.business_id)
      );
      setFavourites(pendingFavourites);
      setWebsites(webResponse.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load favourites');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateWebsite = async (favourite) => {
    setGenerating(favourite.id);
    setGenerationProgress(prev => ({
      ...prev,
      show: true,
      businessName: favourite.business_name,
      currentStep: 0
    }));

    // Simulate progress steps - faster intervals
    let stepIndex = 0;
    const progressInterval = setInterval(() => {
      stepIndex++;
      if (stepIndex < 4) { // Stop at step 4, let completion handle the final step
        setGenerationProgress(prev => ({
          ...prev,
          currentStep: stepIndex
        }));
      }
    }, 1500);

    try {
      // The business should already be saved from when it was favourited
      const response = await axios.post(
        `${API}/websites/generate`,
        { business_id: favourite.business_id },
        { headers }
      );
      
      clearInterval(progressInterval);
      
      // Jump to final step
      setGenerationProgress(prev => ({
        ...prev,
        currentStep: prev.steps.length - 1
      }));
      
      // Brief pause to show completion
      await new Promise(resolve => setTimeout(resolve, 800));
      
      toast.success(`Website generated for ${favourite.business_name}!`);
      setGenerationProgress(prev => ({ ...prev, show: false }));
      fetchData(); // Refresh the list - generated business will disappear
    } catch (error) {
      clearInterval(progressInterval);
      setGenerationProgress(prev => ({ ...prev, show: false }));
      toast.error('Failed to generate website');
    } finally {
      setGenerating(null);
    }
  };

  const handleRemoveFavourite = async (favouriteId) => {
    setRemoving(favouriteId);
    try {
      await axios.delete(`${API}/favourites/${favouriteId}`, { headers });
      toast.success('Removed from favourites');
      setFavourites(favourites.filter(f => f.id !== favouriteId));
      // Also remove from selected if it was selected
      setSelectedIds(prev => prev.filter(id => id !== favouriteId));
    } catch (error) {
      toast.error('Failed to remove from favourites');
    } finally {
      setRemoving(null);
    }
  };

  // Selection handlers
  const toggleSelection = (favouriteId) => {
    setSelectedIds(prev => {
      if (prev.includes(favouriteId)) {
        return prev.filter(id => id !== favouriteId);
      }
      if (prev.length >= MAX_BULK_GENERATE) {
        toast.error(`Maximum ${MAX_BULK_GENERATE} businesses can be selected at once`);
        return prev;
      }
      return [...prev, favouriteId];
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === Math.min(favourites.length, MAX_BULK_GENERATE)) {
      setSelectedIds([]);
    } else {
      setSelectedIds(favourites.slice(0, MAX_BULK_GENERATE).map(f => f.id));
    }
  };

  // Bulk generation handler
  const handleBulkGenerate = async () => {
    if (selectedIds.length === 0) {
      toast.error('Please select at least one business');
      return;
    }

    const selectedFavourites = favourites.filter(f => selectedIds.includes(f.id));
    setBulkGenerating(true);
    setBulkProgress({ current: 0, total: selectedFavourites.length, currentName: '' });
    
    setGenerationProgress(prev => ({
      ...prev,
      show: true,
      isBulk: true,
      bulkCurrent: 0,
      bulkTotal: selectedFavourites.length,
      businessName: selectedFavourites[0]?.business_name || '',
      currentStep: 0
    }));

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < selectedFavourites.length; i++) {
      const favourite = selectedFavourites[i];
      
      // Update progress modal
      setGenerationProgress(prev => ({
        ...prev,
        businessName: favourite.business_name,
        bulkCurrent: i + 1,
        currentStep: 0
      }));

      // Simulate progress steps
      let stepIndex = 0;
      const progressInterval = setInterval(() => {
        stepIndex++;
        if (stepIndex < 4) {
          setGenerationProgress(prev => ({
            ...prev,
            currentStep: stepIndex
          }));
        }
      }, 1200);

      try {
        await axios.post(
          `${API}/websites/generate`,
          { business_id: favourite.business_id },
          { headers }
        );
        
        clearInterval(progressInterval);
        
        // Show completion briefly
        setGenerationProgress(prev => ({
          ...prev,
          currentStep: prev.steps.length - 1
        }));
        
        await new Promise(resolve => setTimeout(resolve, 500));
        successCount++;
      } catch (error) {
        clearInterval(progressInterval);
        failCount++;
        console.error(`Failed to generate for ${favourite.business_name}:`, error);
      }
    }

    // Close modal and show results
    setGenerationProgress(prev => ({ ...prev, show: false, isBulk: false }));
    setBulkGenerating(false);
    setSelectedIds([]);

    if (successCount > 0) {
      toast.success(`Successfully generated ${successCount} website${successCount > 1 ? 's' : ''}!`);
    }
    if (failCount > 0) {
      toast.error(`Failed to generate ${failCount} website${failCount > 1 ? 's' : ''}`);
    }

    fetchData(); // Refresh the list - generated businesses will disappear
  };

  // Check if website already exists for a business - used for filtering
  // The getExistingWebsite function is no longer needed since we filter in fetchData

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="favourites-page">
      <Card className="bg-card/50 border-white/5">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500" />
                To Action ({favourites.length})
              </CardTitle>
              <CardDescription>
                Businesses you&apos;ve saved for website generation. Generate websites for these leads.
              </CardDescription>
            </div>
            {favourites.length > 0 && (
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleSelectAll}
                  className="gap-2"
                  data-testid="select-all-btn"
                >
                  {selectedIds.length === Math.min(favourites.length, MAX_BULK_GENERATE) ? (
                    <>
                      <CheckSquare className="w-4 h-4" />
                      Deselect All
                    </>
                  ) : (
                    <>
                      <Square className="w-4 h-4" />
                      Select All (max {MAX_BULK_GENERATE})
                    </>
                  )}
                </Button>
                {selectedIds.length > 0 && (
                  <Button
                    onClick={handleBulkGenerate}
                    disabled={bulkGenerating}
                    className="gap-2"
                    data-testid="bulk-generate-btn"
                  >
                    {bulkGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Generate Selected ({selectedIds.length})
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {favourites.length === 0 ? (
            <div className="py-16 text-center">
              <Star className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No favourites yet</h3>
              <p className="text-muted-foreground max-w-md mx-auto mb-4">
                Search for businesses and add them to your favourites to generate websites.
              </p>
              <Button onClick={() => navigate('/admin/find')} className="gap-2">
                Find Businesses
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {favourites.map((favourite) => {
                const CategoryIcon = getCategoryIcon(favourite.category);
                const isSelected = selectedIds.includes(favourite.id);
                
                return (
                  <div
                    key={favourite.id}
                    className={`bg-background/50 border rounded-xl p-5 flex flex-col md:flex-row md:items-center gap-4 transition-all cursor-pointer ${
                      isSelected 
                        ? 'border-primary/50 bg-primary/5' 
                        : 'border-white/5 hover:border-white/10'
                    }`}
                    onClick={() => toggleSelection(favourite.id)}
                    data-testid={`favourite-${favourite.id}`}
                  >
                    {/* Checkbox */}
                    <div 
                      className="flex items-center justify-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSelection(favourite.id)}
                        className="w-5 h-5"
                        data-testid={`checkbox-${favourite.id}`}
                      />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                          <CategoryIcon className="w-6 h-6 text-amber-500" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg">{favourite.business_name}</h4>
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" /> {formatAddress(favourite.address)}
                          </p>
                          {favourite.phone && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                              <Phone className="w-3 h-3" /> {favourite.phone}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-sm flex items-center gap-1">
                              <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                              <span className="font-medium">{favourite.rating}</span>
                              <span className="text-muted-foreground">({favourite.review_count} reviews)</span>
                            </span>
                            <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                              {formatCategory(favourite.category)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        onClick={() => handleGenerateWebsite(favourite)}
                        disabled={generating === favourite.id || bulkGenerating}
                        className="gap-2"
                        data-testid={`generate-${favourite.id}`}
                      >
                        {generating === favourite.id ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4" />
                            Generate Website
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleRemoveFavourite(favourite.id)}
                        disabled={removing === favourite.id}
                        className="text-destructive hover:text-destructive"
                        data-testid={`remove-${favourite.id}`}
                      >
                        {removing === favourite.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generation Progress Modal */}
      <Dialog open={generationProgress.show} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" hideClose>
          <DialogHeader>
            <DialogTitle className="text-center">
              {generationProgress.isBulk 
                ? `Generating Websites (${generationProgress.bulkCurrent}/${generationProgress.bulkTotal})`
                : 'Generating Website'
              }
            </DialogTitle>
          </DialogHeader>
          <div className="py-6">
            {/* Bulk progress bar */}
            {generationProgress.isBulk && (
              <div className="mb-6">
                <div className="flex justify-between text-sm text-muted-foreground mb-2">
                  <span>Overall Progress</span>
                  <span>{generationProgress.bulkCurrent} of {generationProgress.bulkTotal}</span>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: `${(generationProgress.bulkCurrent / generationProgress.bulkTotal) * 100}%` }}
                  />
                </div>
              </div>
            )}
            
            <p className="text-center text-muted-foreground mb-6">
              {generationProgress.isBulk ? 'Currently creating' : 'Creating website for'}{' '}
              <span className="font-semibold text-foreground">{generationProgress.businessName}</span>
            </p>
            
            <div className="space-y-4">
              {generationProgress.steps.map((step, index) => {
                const StepIcon = step.icon;
                const isComplete = index < generationProgress.currentStep;
                const isCurrent = index === generationProgress.currentStep;
                
                return (
                  <div 
                    key={step.id}
                    className={`flex items-center gap-4 p-3 rounded-lg transition-all ${
                      isCurrent ? 'bg-primary/10 border border-primary/20' :
                      isComplete ? 'bg-muted/50' : 'opacity-50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isComplete ? 'bg-primary text-primary-foreground' :
                      isCurrent ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                    }`}>
                      {isComplete ? (
                        <CheckCircle2 className="w-5 h-5" />
                      ) : isCurrent ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <StepIcon className="w-5 h-5" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${isCurrent ? 'text-primary' : ''}`}>
                        {step.name}
                      </p>
                      {isCurrent && (
                        <p className="text-xs text-muted-foreground">In progress...</p>
                      )}
                      {isComplete && (
                        <p className="text-xs text-muted-foreground">Complete</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <p className="text-center text-xs text-muted-foreground mt-6">
              {generationProgress.isBulk 
                ? `Generating ${generationProgress.bulkTotal} websites. This may take a few minutes.`
                : "This usually takes 10-30 seconds. Please don't close this page."
              }
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Favourites;
